import { useNavigate } from "react-router-dom";
import { Search, BookOpen, PenTool, Compass } from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";

interface QuickLinkProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  href: string;
  gradient: string;
  index: number;
}

function QuickLink({
  icon,
  title,
  description,
  href,
  gradient,
  index,
}: QuickLinkProps) {
  const navigate = useNavigate();

  return (
    <motion.button
      onClick={() => navigate(href)}
      className="group relative w-full text-left"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{
        duration: 0.4,
        delay: index * 0.08,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      whileHover={{ y: -4 }}
    >
      <div className="border-border/40 relative h-full rounded-xl border bg-white p-4 shadow-sm transition-shadow duration-300 hover:shadow-lg sm:p-5">
        <div
          className={`h-10 w-10 ${gradient} mb-3 flex items-center justify-center rounded-lg shadow-md transition-transform duration-300 group-hover:scale-105 sm:h-11 sm:w-11`}
        >
          {icon}
        </div>
        <h3 className="text-foreground group-hover:text-primary mb-1 font-serif text-lg font-bold transition-colors sm:text-xl">
          {title}
        </h3>
        <p className="text-muted-foreground text-sm leading-snug sm:text-base sm:leading-relaxed">
          {description}
        </p>
        <div className="bg-secondary/80 absolute bottom-3.5 right-3.5 flex h-6 w-6 translate-x-1 items-center justify-center rounded-full opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100 sm:h-7 sm:w-7 sm:bottom-4 sm:right-4">
          <svg
            className="text-primary h-3 w-3 sm:h-3.5 sm:w-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </div>
      </div>
    </motion.button>
  );
}

export function QuickLinks() {
  const { t } = useLanguage();
  const links = [
    {
      icon: <Search className="h-5 w-5 text-white" />,
      title: t("quickLinks.searchTitle"),
      description: t("quickLinks.searchDesc"),
      href: "/search",
      gradient: "bg-gradient-to-br from-primary to-primary/70",
    },
    {
      icon: <BookOpen className="h-5 w-5 text-white" />,
      title: t("quickLinks.browseTitle"),
      description: t("quickLinks.browseDesc"),
      href: "/browse",
      gradient: "bg-gradient-to-br from-accent to-accent/70",
    },
    {
      icon: <PenTool className="h-5 w-5 text-white" />,
      title: t("nav.contribute"),
      description: t("quickLinks.contributeDesc"),
      href: "/contribute",
      gradient: "bg-gradient-to-br from-gold to-gold/70",
    },
    {
      icon: <Compass className="h-5 w-5 text-white" />,
      title: t("quickLinks.exploreTitle"),
      description: t("quickLinks.exploreDesc"),
      href: "/browse",
      gradient:
        "bg-gradient-to-br from-[hsl(200,45%,30%)] to-[hsl(200,45%,40%)]",
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-3.5 lg:grid-cols-4">
      {links.map((link, index) => (
        <QuickLink
          key={link.href + link.title}
          icon={link.icon}
          title={link.title}
          description={link.description}
          href={link.href}
          gradient={link.gradient}
          index={index}
        />
      ))}
    </div>
  );
}
